import { useState, useEffect, useCallback, FC } from 'react';
import {
  Ticket,
  Plus,
  Edit2,
  Trash2,
  Search,
  CheckCircle2,
  Clock,
  Filter,
  Check,
  Power,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Drawer } from '../ui/Drawer';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { PageHeader } from '../widgets/PageHeader';
import { MetricWidget } from '../widgets/MetricWidget';
import { DataTable, Column } from '../widgets/DataTable';
import { LoadingSkeleton } from '../ui/LoadingSkeleton';
import {
  fetchCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  Coupon,
  CouponCreatePayload,
} from '../../api/coupons';

const MARKETPLACES = [
  { id: 'all', label: 'Todos' },
  { id: 'amazon', label: 'Amazon' },
  { id: 'mercadolivre', label: 'Mercado Livre' },
  { id: 'shopee', label: 'Shopee' },
  { id: 'magalu', label: 'Magalu' },
  { id: 'other', label: 'Outros' },
];

export const CouponsView: FC = () => {
  const { token } = useAuth();
  const { success, error: showError } = useToast();

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMarketplace, setSelectedMarketplace] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Estado do Drawer de Criação/Edição
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Campos do formulário
  const [formCode, setFormCode] = useState('');
  const [formDiscountType, setFormDiscountType] = useState<'percent' | 'fixed'>(
    'percent'
  );
  const [formDiscountValue, setFormDiscountValue] = useState<string>('10');
  const [formMinOrderValue, setFormMinOrderValue] = useState<string>('');
  const [formMarketplace, setFormMarketplace] = useState<string>('all');
  const [formExpiresAt, setFormExpiresAt] = useState<string>('');
  const [formIsActive, setFormIsActive] = useState<boolean>(true);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Estado do Modal de Exclusão
  const [deletingCoupon, setDeletingCoupon] = useState<Coupon | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadCoupons = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const data = await fetchCoupons(token, {
        marketplace:
          selectedMarketplace !== 'all' ? selectedMarketplace : undefined,
        search: searchQuery.trim() || undefined,
      });
      setCoupons(data);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao buscar cupons.';
      showError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedMarketplace, searchQuery, showError]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const handleOpenDrawer = (coupon?: Coupon) => {
    setFormErrors({});
    if (coupon) {
      setEditingCoupon(coupon);
      setFormCode(coupon.code);
      setFormDiscountType(coupon.discount_type);
      setFormDiscountValue(String(coupon.discount_value));
      setFormMinOrderValue(
        coupon.min_order_value !== null && coupon.min_order_value !== undefined
          ? String(coupon.min_order_value)
          : ''
      );
      setFormMarketplace(coupon.marketplace || 'all');
      setFormExpiresAt(
        coupon.expires_at ? coupon.expires_at.substring(0, 16) : ''
      );
      setFormIsActive(coupon.is_active);
    } else {
      setEditingCoupon(null);
      setFormCode('');
      setFormDiscountType('percent');
      setFormDiscountValue('10');
      setFormMinOrderValue('');
      setFormMarketplace('all');
      setFormExpiresAt('');
      setFormIsActive(true);
    }
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingCoupon(null);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formCode.trim()) {
      errs.code = 'O código do cupom é obrigatório.';
    }
    const val = parseFloat(formDiscountValue);
    if (isNaN(val) || val <= 0) {
      errs.discountValue =
        'Informe um valor de desconto válido maior que zero.';
    } else if (formDiscountType === 'percent' && val > 100) {
      errs.discountValue = 'O desconto percentual não pode ultrapassar 100%.';
    }

    if (formMinOrderValue.trim()) {
      const minVal = parseFloat(formMinOrderValue);
      if (isNaN(minVal) || minVal < 0) {
        errs.minOrderValue = 'O valor mínimo deve ser maior ou igual a zero.';
      }
    }

    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveCoupon = async () => {
    if (!token || !validateForm()) return;
    setIsSaving(true);
    try {
      const payload: CouponCreatePayload = {
        code: formCode.trim().toUpperCase(),
        discount_type: formDiscountType,
        discount_value: parseFloat(formDiscountValue),
        min_order_value: formMinOrderValue.trim()
          ? parseFloat(formMinOrderValue)
          : null,
        marketplace: formMarketplace,
        expires_at: formExpiresAt
          ? new Date(formExpiresAt).toISOString()
          : null,
        is_active: formIsActive,
      };

      if (editingCoupon) {
        await updateCoupon(token, editingCoupon.id, payload);
        success('Cupom atualizado com sucesso.', 'Catálogo Atualizado');
      } else {
        await createCoupon(token, payload);
        success('Cupom cadastrado com sucesso.', 'Cupom Adicionado');
      }
      handleCloseDrawer();
      await loadCoupons();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Falha ao salvar cupom.';
      showError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (coupon: Coupon) => {
    if (!token) return;
    try {
      await updateCoupon(token, coupon.id, { is_active: !coupon.is_active });
      success(
        `Cupom ${coupon.code} ${!coupon.is_active ? 'ativado' : 'desativado'}.`
      );
      await loadCoupons();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao alterar status.';
      showError(msg);
    }
  };

  const handleDeleteCoupon = async () => {
    if (!token || !deletingCoupon) return;
    setIsDeleting(true);
    try {
      await deleteCoupon(token, deletingCoupon.id);
      success('Cupom excluído com sucesso.', 'Catálogo Atualizado');
      setDeletingCoupon(null);
      await loadCoupons();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Falha ao excluir cupom.';
      showError(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  // Métricas
  const totalCount = coupons.length;
  const activeCount = coupons.filter((c) => c.is_active).length;
  const percentCount = coupons.filter(
    (c) => c.discount_type === 'percent'
  ).length;
  const fixedCount = coupons.filter((c) => c.discount_type === 'fixed').length;

  const columns: Column<Coupon>[] = [
    {
      key: 'code',
      header: 'Código do Cupom',
      render: (coupon) => (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-brand-500/20 bg-brand-500/10 px-2.5 py-1 font-mono text-xs font-bold text-brand-600 dark:text-brand-400">
            <Ticket className="h-3.5 w-3.5" />
            {coupon.code}
          </span>
        </div>
      ),
    },
    {
      key: 'discount',
      header: 'Desconto',
      render: (coupon) => (
        <span className="font-bold text-content-main">
          {coupon.discount_type === 'percent'
            ? `${coupon.discount_value}% OFF`
            : coupon.discount_value.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Tipo',
      render: (coupon) => (
        <span className="text-2xs text-content-muted">
          {coupon.discount_type === 'percent' ? 'Percentual' : 'Valor Fixo'}
        </span>
      ),
    },
    {
      key: 'marketplace',
      header: 'Marketplace',
      render: (coupon) => (
        <Badge variant="neutral">
          {coupon.marketplace === 'all'
            ? 'Todos'
            : coupon.marketplace.toUpperCase()}
        </Badge>
      ),
    },
    {
      key: 'min_order',
      header: 'Pedido Mínimo',
      render: (coupon) => (
        <span className="text-xs text-content-muted">
          {coupon.min_order_value
            ? coupon.min_order_value.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })
            : 'Sem mínimo'}
        </span>
      ),
    },
    {
      key: 'expires_at',
      header: 'Validade',
      render: (coupon) => {
        if (!coupon.expires_at) {
          return (
            <span className="text-xs text-content-muted">Indeterminada</span>
          );
        }
        const expDate = new Date(coupon.expires_at);
        const isExpired = expDate < new Date();
        return (
          <div className="flex items-center gap-1.5">
            <span
              className={`text-xs ${isExpired ? 'font-semibold text-rose-500' : 'text-content-muted'}`}
            >
              {expDate.toLocaleDateString('pt-BR')}
            </span>
            {isExpired && <Badge variant="offline">Expirado</Badge>}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (coupon) => (
        <Badge variant={coupon.is_active ? 'online' : 'offline'}>
          {coupon.is_active ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (coupon) => (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            title={coupon.is_active ? 'Desativar cupom' : 'Ativar cupom'}
            onClick={() => handleToggleActive(coupon)}
            aria-label={`Alternar status ${coupon.code}`}
          >
            <Power
              className={`h-3.5 w-3.5 ${coupon.is_active ? 'text-brand-500' : 'text-content-muted'}`}
            />
          </Button>

          <Button
            size="sm"
            variant="ghost"
            title="Editar cupom"
            onClick={() => handleOpenDrawer(coupon)}
            aria-label={`Editar ${coupon.code}`}
          >
            <Edit2 className="h-3.5 w-3.5 text-content-muted hover:text-content-main" />
          </Button>

          <Button
            size="sm"
            variant="ghost"
            title="Excluir cupom"
            onClick={() => setDeletingCoupon(coupon)}
            aria-label={`Excluir ${coupon.code}`}
          >
            <Trash2 className="h-3.5 w-3.5 text-content-muted hover:text-rose-500" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catálogo de Cupons"
        subtitle="Gerencie cupons reutilizáveis com cálculo automático de desconto e formatação no WhatsApp"
        actions={
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => handleOpenDrawer()}
          >
            Novo Cupom
          </Button>
        }
      />

      {/* Grid de Métricas */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricWidget
          label="Total de Cupons"
          value={totalCount}
          icon={Ticket}
          trend={
            totalCount > 0
              ? { value: `${totalCount} cupons`, positive: true }
              : undefined
          }
        />
        <MetricWidget
          label="Cupons Ativos"
          value={activeCount}
          icon={CheckCircle2}
          trend={
            activeCount > 0
              ? { value: `${activeCount} ativos`, positive: true }
              : undefined
          }
        />
        <MetricWidget
          label="Desconto %"
          value={percentCount}
          icon={Filter}
          trend={
            percentCount > 0
              ? { value: `${percentCount} cupons`, positive: null }
              : undefined
          }
        />
        <MetricWidget
          label="Desconto Fixo (R$)"
          value={fixedCount}
          icon={Clock}
          trend={
            fixedCount > 0
              ? { value: `${fixedCount} cupons`, positive: null }
              : undefined
          }
        />
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="flex flex-col gap-3 rounded-2xl border border-surface-border bg-surface-panel p-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Marketplace chips */}
        <div className="flex flex-wrap items-center gap-1.5">
          {MARKETPLACES.map((m) => {
            const isSelected = selectedMarketplace === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelectedMarketplace(m.id)}
                className={`cursor-pointer rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
                  isSelected
                    ? 'bg-brand-500 text-white'
                    : 'bg-surface-subtle text-content-muted hover:text-content-main'
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Campo de Busca */}
        <div className="w-full sm:w-64">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por código..."
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Tabela de Cupons */}
      {isLoading ? (
        <LoadingSkeleton variant="table" count={4} />
      ) : coupons.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-surface-border bg-surface-panel p-12 text-center">
          <Ticket className="mx-auto h-12 w-12 text-content-muted" />
          <h3 className="mt-3 text-base font-bold text-content-main">
            Nenhum cupom cadastrado
          </h3>
          <p className="mx-auto mt-1 max-w-sm text-xs text-content-muted">
            Cadastre cupons reutilizáveis de marketplaces para aplicá-los com 1
            clique às suas ofertas e itens da fila.
          </p>
          <div className="mt-4">
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => handleOpenDrawer()}
            >
              Criar Primeiro Cupom
            </Button>
          </div>
        </div>
      ) : (
        <DataTable
          data={coupons}
          columns={columns}
          keyExtractor={(item) => item.id}
          pageSize={10}
        />
      )}

      {/* Drawer de Cadastro/Edição de Cupom */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        title={editingCoupon ? 'Editar Cupom' : 'Novo Cupom de Desconto'}
        description="Configure as regras e o marketplace associado ao cupom"
      >
        <div className="space-y-4">
          <Input
            label="Código do Cupom"
            value={formCode}
            onChange={(e) => setFormCode(e.target.value.toUpperCase())}
            placeholder="Ex: 10OFF, VALE20"
            error={formErrors.code}
            leftIcon={<Ticket className="h-4 w-4" />}
          />

          <div>
            <label className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-content-muted">
              Tipo de Desconto
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormDiscountType('percent')}
                className={`cursor-pointer rounded-xl border p-2.5 text-center text-xs font-semibold transition-colors ${
                  formDiscountType === 'percent'
                    ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400'
                    : 'border-surface-border bg-surface-subtle text-content-muted hover:text-content-main'
                }`}
              >
                Porcentagem (%)
              </button>
              <button
                type="button"
                onClick={() => setFormDiscountType('fixed')}
                className={`cursor-pointer rounded-xl border p-2.5 text-center text-xs font-semibold transition-colors ${
                  formDiscountType === 'fixed'
                    ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400'
                    : 'border-surface-border bg-surface-subtle text-content-muted hover:text-content-main'
                }`}
              >
                Valor Fixo (R$)
              </button>
            </div>
          </div>

          <Input
            label={
              formDiscountType === 'percent'
                ? 'Porcentagem de Desconto (%)'
                : 'Valor do Desconto (R$)'
            }
            type="number"
            value={formDiscountValue}
            onChange={(e) => setFormDiscountValue(e.target.value)}
            placeholder={formDiscountType === 'percent' ? '10' : '20.00'}
            error={formErrors.discountValue}
          />

          <Input
            label="Pedido Mínimo (R$ - opcional)"
            type="number"
            value={formMinOrderValue}
            onChange={(e) => setFormMinOrderValue(e.target.value)}
            placeholder="Ex: 50.00"
            error={formErrors.minOrderValue}
          />

          <div>
            <label className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-content-muted">
              Marketplace
            </label>
            <select
              value={formMarketplace}
              onChange={(e) => setFormMarketplace(e.target.value)}
              className="focus:outline-hidden w-full rounded-xl border border-surface-border bg-surface-subtle px-3 py-2 text-xs text-content-main transition-colors focus:border-brand-500"
            >
              <option value="all">Todos os Marketplaces</option>
              <option value="amazon">Amazon</option>
              <option value="mercadolivre">Mercado Livre</option>
              <option value="shopee">Shopee</option>
              <option value="magalu">Magalu</option>
              <option value="other">Outros</option>
            </select>
          </div>

          <Input
            label="Data de Validade (opcional)"
            type="datetime-local"
            value={formExpiresAt}
            onChange={(e) => setFormExpiresAt(e.target.value)}
          />

          <div className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-subtle p-3">
            <div>
              <p className="text-xs font-semibold text-content-main">
                Status Ativo
              </p>
              <p className="text-2xs text-content-muted">
                Cupons inativos não aparecem na seleção rápida
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFormIsActive(!formIsActive)}
              className={`focus:outline-hidden relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                formIsActive ? 'bg-brand-500' : 'bg-surface-border'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                  formIsActive ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-surface-border pt-4">
            <Button
              variant="ghost"
              onClick={handleCloseDrawer}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveCoupon}
              isLoading={isSaving}
              leftIcon={<Check className="h-4 w-4" />}
            >
              {editingCoupon ? 'Salvar Alterações' : 'Cadastrar Cupom'}
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmationModal
        isOpen={Boolean(deletingCoupon)}
        onClose={() => setDeletingCoupon(null)}
        onConfirm={handleDeleteCoupon}
        title="Excluir Cupom do Catálogo"
        description={`Tem certeza que deseja excluir o cupom '${deletingCoupon?.code}'? Esta ação é irreversível.`}
        confirmText="Excluir Definitivamente"
        isLoading={isDeleting}
      />
    </div>
  );
};
